<template>
  <o-dialog
    ref="dialog"
    :title="$t('pass.emailCustomer')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.send'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
  >
    <div class="pt-4 text-left -mx-4 bg-gray-50 border-t border-b px-4">
      <o-form v-if="customerPass" ref="formRef">
        <o-field-group
          :label="$t('general.preset')"
          :name="$t('general.preset')"
          class="mb-4 block"
        >
          <segment-field v-model="preset" :options="[ { value: 'Recipient', label: $t('general.recipient') }, { value: 'Purchaser', label: $t('general.purchaser') }, { value: 'Other', label: $t('general.other') } ]" />
        </o-field-group>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.toName')"
          :name="$t('general.toName')"
          class="mb-4 block"
          rules="required"
        >
          <text-field v-model="toName" :status="status" />
        </o-field-group>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.toEmail')"
          :name="$t('general.toEmail')"
          class="mb-4 block"
          rules="required|email"
        >
          <text-field v-model="toEmail" :status="status" />
        </o-field-group>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.fromName')"
          :name="$t('general.fromName')"
          class="mb-4 block"
          rules="required"
        >
          <text-field v-model="fromName" :status="status" />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  TextField,
  SegmentField,
} from '@/components/fields'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
}

withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const formRef = ref<any>()
const customerPass = ref<any>(null)
const preset = ref('Recipient')
const toName = ref('')
const toEmail = ref('')
const fromName = ref('')
const body = ref('')

const info = computed(() => {
  return $i18n.t('components.customerPass.actions.email.info', { id: customerPass.value?.idCustomerPass })
})

watch(preset, (presetValue) => {
  switch (presetValue) {
    case 'Recipient':
      toName.value = customerPass.value.gift?.to?.name || ''
      toEmail.value = customerPass.value.gift?.to?.email || ''
      fromName.value = customerPass.value.gift?.from?.name || ''
      body.value = customerPass.value.gift?.message || ''
      break

    case 'Purchaser':
      toName.value = customerPass.value.order?.customer.displayName || ''
      toEmail.value = customerPass.value.order?.customer.email || ''
      break

    default:
      toName.value = ''
      toEmail.value = ''
      break
  }
})

const open = (customerPassArg: any) => {
  customerPass.value = customerPassArg

  preset.value = 'Recipient'
  toName.value = customerPassArg.gift?.to?.name || ''
  toEmail.value = customerPassArg.gift?.to?.email || ''
  fromName.value = customerPassArg.gift?.from?.name || ''
  body.value = customerPassArg.gift?.message || ''

  dialog.value.open()
}

const confirm = async () => {
  emit('update:loading', true)

  try {
    const { fromName: from, toName: to, toEmail: email, body: msg } = {
      fromName: fromName.value,
      toName: toName.value,
      toEmail: toEmail.value,
      body: body.value,
    }
    const response = await $api.customerPass.email(customerPass.value.idCustomerPass?.toString(), { fromName: from, toName: to, toEmail: email, body: msg })
    const success = response.success ?? response

    if (success) {
      emit('success')

      $notification.toast({
        title: $i18n.t('general.sentEmail'),
        text: $i18n.t('components.customerPass.actions.email.successInfo'),
        variant: 'success',
      })

      emit('update:loading', false)

      return true
    }
  } catch (e) {
    console.log(e)
    $notification.toast({
      title: $i18n.t('general.error'),
      text: $i18n.t('components.customerPass.actions.email.errorInfo'),
    })
  }

  emit('update:loading', false)
}

defineExpose({ open })
</script>
